import { startOfDay, startOfMonth } from 'date-fns';
import { SortType } from 'utils/constants/common';
import { previousMonth } from 'utils/date';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

type Sorts = Map<string, SortType>;
type IsLoad = boolean;
type CurrentDate = Date;
type FromDate = Date;
type ToDate = Date;

interface Invoice {
    id: number;
    amount: number;
}

export enum ActType {
    All = 'ALL',
    Clickme = 'CLICKME',
    HH = 'HH',
}

export enum ActStatus {
    All = 'ALL',
    New = 'NEW',
    ReadyTo1C = 'READY_TO_1C',
    Waiting1C = 'WAITING_1C',
    Finished = 'FINISHED',
    SendingTo1C = 'SENDING_TO_1C',
    SentTo1C = 'SENT_TO_1C',
    Error = 'ERROR',
    Verified = 'VERIFIED',
    VerifiedError = 'VERIFIED_ERROR',
    Invalid = 'INVALID',
}

export enum ActSendStatus {
    All = 'ALL',
    New = 'NEW',
    Sent = 'SENT',
    Failed = 'FAILED',
    Ignored = 'IGNORED',
}

export enum ActProcessingType {
    All = 'ALL',
    Manual = 'MANUAL',
    Auto = 'AUTO',
}

export enum ActAdvertisingServiceType {
    All = 'ALL',
    NotMarked = 'NOT_MARKED',
    Advertising = 'ADVERTISING',
    Information = 'INFORMATION',
}

export enum ActProductType {
    All = 'ALL',
    Cpc = 'CLICKME_CPC',
    Auction = 'CLICKME_SEARCH_AUCTION',
    Segments = 'CLICKME_SEGMENTS',
}

export interface Act {
    actId: number;
    accountId: number;
    actName: string;
    amount: number;
    correctedAmount: number | null;
    actDate: string;
    employerId: number;
    error: string | null;
    invoices: Invoice[];
    statisticAmount: number;
    status: ActStatus;
    type: ActType;
    processingType: ActProcessingType;
    advertisingServiceType: ActAdvertisingServiceType;
    documentNumber: string;
    period: string;
    periodStart: string;
    sentAt: string;
    actSendStatus: ActSendStatus;
}

// ----------------------- actions -------------------------------------
const SET_ACTS = 'SET_ACTS';
const SET_ACTS_DATES = 'SET_ACTS_DATES';
const SET_SORT_ACTS = 'SET_SORT_ACTS';
const SET_IS_LOAD_ACTS = 'SET_IS_LOAD_ACTS';
const SET_ACTS_CURRENT_DATE = 'SET_ACTS_CURRENT_DATE';
const SET_ACTS_STATUSES = 'SET_ACTS_STATUSES';
const SET_ACTS_TYPES = 'SET_ACTS_TYPES';
const SET_ACTS_ADVERTISING_SERVICE_TYPE = 'SET_ACTS_ADVERTISING_SERVICE_TYPE';
const SET_ACTS_PROCESSING_TYPE = 'SET_ACTS_PROCESSING_TYPE';
const SET_EMPLOYER_ID = 'SET_EMPLOYER_ID';
const SET_ACCOUNT_ID = 'SET_ACCOUNT_ID';
const UPDATE_ACT = 'UPDATE_ACT';
const RESET_ACTS_FILTERS = 'RESET_ACTS_FILTERS';

interface PayloadTypes {
    [SET_ACTS]: Act[];
    [SET_ACTS_DATES]: { from: FromDate; to: ToDate };
    [SET_SORT_ACTS]: Sorts;
    [SET_IS_LOAD_ACTS]: IsLoad;
    [SET_ACTS_CURRENT_DATE]: CurrentDate;
    [SET_ACTS_STATUSES]: ActStatus;
    [SET_ACTS_TYPES]: ActType;
    [SET_ACTS_ADVERTISING_SERVICE_TYPE]: ActAdvertisingServiceType;
    [SET_ACTS_PROCESSING_TYPE]: ActProcessingType;
    [SET_EMPLOYER_ID]: number;
    [SET_ACCOUNT_ID]: number;
    [UPDATE_ACT]: { actId: number; newActData: Act };
    [RESET_ACTS_FILTERS]: void;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setActs = ActionCreator(SET_ACTS);
export const setDates = ActionCreator(SET_ACTS_DATES);
export const setSortActs = ActionCreator(SET_SORT_ACTS);
export const setLoadActs = ActionCreator(SET_IS_LOAD_ACTS);
export const setCurrentDate = ActionCreator(SET_ACTS_CURRENT_DATE);
export const setStatuses = ActionCreator(SET_ACTS_STATUSES);
export const setTypes = ActionCreator(SET_ACTS_TYPES);
export const setAdvertisingServiceType = ActionCreator(SET_ACTS_ADVERTISING_SERVICE_TYPE);
export const setProcessingType = ActionCreator(SET_ACTS_PROCESSING_TYPE);
export const setEmployerId = ActionCreator(SET_EMPLOYER_ID);
export const setAccountId = ActionCreator(SET_ACCOUNT_ID);
export const updateAct = ActionCreator(UPDATE_ACT);
export const resetFilters = ActionCreator(RESET_ACTS_FILTERS);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
interface ActsState {
    acts: Act[];
    sorts: Sorts;
    isLoad: IsLoad;
    currentDate: CurrentDate;
    from: FromDate;
    to: ToDate;
    statuses: ActStatus;
    selectTypes: ActType;
    advertisingServiceType: ActAdvertisingServiceType;
    processingType: ActProcessingType;
    employerId: number | null;
    accountId: number | null;
}

export const ACTS_INITIAL_STATE: ActsState = {
    acts: [],
    sorts: new Map(),
    isLoad: false,
    currentDate: startOfMonth(previousMonth),
    from: new Date(previousMonth),
    to: startOfDay(new Date()),
    statuses: ActStatus.All,
    selectTypes: ActType.All,
    advertisingServiceType: ActAdvertisingServiceType.All,
    processingType: ActProcessingType.All,
    employerId: null,
    accountId: null,
};

export default createReducer<ActsState, PayloadTypes>(ACTS_INITIAL_STATE, {
    [SET_ACTS]: (state, { payload }) => ({
        ...state,
        acts: payload,
    }),
    [SET_ACTS_DATES]: (state, { payload }) => ({
        ...state,
        from: payload.from,
        to: payload.to,
    }),
    [SET_SORT_ACTS]: (state, { payload }) => ({
        ...state,
        sorts: payload,
    }),
    [SET_IS_LOAD_ACTS]: (state, { payload }) => ({
        ...state,
        isLoad: payload,
    }),
    [SET_ACTS_CURRENT_DATE]: (state, { payload }) => ({
        ...state,
        currentDate: payload,
    }),
    [SET_ACTS_STATUSES]: (state, { payload }) => ({
        ...state,
        statuses: payload,
    }),
    [SET_ACTS_TYPES]: (state, { payload }) => ({
        ...state,
        selectTypes: payload,
    }),
    [SET_ACTS_ADVERTISING_SERVICE_TYPE]: (state, { payload }) => ({
        ...state,
        advertisingServiceType: payload,
    }),
    [SET_ACTS_PROCESSING_TYPE]: (state, { payload }) => ({
        ...state,
        processingType: payload,
    }),
    [SET_EMPLOYER_ID]: (state, { payload }) => ({
        ...state,
        employerId: payload,
    }),
    [SET_ACCOUNT_ID]: (state, { payload }) => ({
        ...state,
        accountId: payload,
    }),
    [UPDATE_ACT]: (state, { payload }) => {
        const newActs = [...state.acts];
        const indexForNewAct = newActs.findIndex(({ actId }) => actId === payload.actId);
        newActs[indexForNewAct] = payload.newActData;
        return {
            ...state,
            acts: newActs,
        };
    },
    [RESET_ACTS_FILTERS]: (state) => ({
        ...state,
        currentDate: ACTS_INITIAL_STATE.currentDate,
        statuses: ACTS_INITIAL_STATE.statuses,
        selectTypes: ACTS_INITIAL_STATE.selectTypes,
        advertisingServiceType: ACTS_INITIAL_STATE.advertisingServiceType,
        processingType: ACTS_INITIAL_STATE.processingType,
        employerId: ACTS_INITIAL_STATE.employerId,
        accountId: ACTS_INITIAL_STATE.accountId,
        to: ACTS_INITIAL_STATE.to,
        from: ACTS_INITIAL_STATE.from,
    }),
});
// ----------------------- /reducer --------------------------------------
